import { Loader2 } from 'lucide-react';
import React from 'react';

/**
 * DataGridMobile - Vista de tarjetas optimizada para tablets y móviles.
 */
const EMPTY_IDS = [];

const DataGridMobile = ({
    data,
    columns,
    selectable,
    selectedIds = EMPTY_IDS,
    onSelectAll,
    onSelectRow = () => { },
    hideSelectionOnMobile,
    renderMobileItem,
    viewMode,
    hasMore,
    loadingMore,
    onLoadMore,
    loadMoreText,
    headerActions,
    className
}) => {
    // Renderizado base de tarjeta premium inteligente (Identidad -> Rejilla Adaptativa)
    const renderMobileCard = (item, index) => {
        // 1. Identification Logic (Proactive Detection)
        const findCol = (headers, keys = []) => columns.find(col => {
            const h = (col.header || '').toString().toUpperCase();
            const k = (col.key || '').toString().toUpperCase();
            return headers.some(header => h.includes(header)) || keys.some(key => k.includes(key));
        }) || null;

        const skuCol = findCol(['SKU'], ['SKU']);
        const statusCol = findCol(['STATUS', 'SEVERITY', 'ESTADO'], ['STATUS', 'SEVERITY', 'ACCOUNT_STATUS']);
        const itemCol = findCol(['ITEM', 'PRODUCT', 'ASSET', 'NAME', 'DESCRIPCIÓN', 'USER INFO', 'NOMBRE'], ['ITEM', 'PRODUCT', 'ASSET', 'NAME', 'DESCRIPTION', 'DISPLAYNAME', 'EMAIL']);
        const locationCol = findCol(['LOCATION', 'UBICACIÓN', 'ROLE', 'ROL'], ['LOCATION', 'UBICACION', 'ROLE', 'ROL']);
        const stockCol = findCol(['STOCK', 'CANTIDAD', 'LAST ACCESS', 'ÚLTIMO ACCESO', 'ACCESO'], ['STOCK', 'QUANTITY', 'STOCK_CURRENT', 'LASTLOGIN', 'ACCESS']);
        const codeCol = findCol(['CODE', 'CÓDIGO', 'WITHDRAWAL CODE'], ['CODE', 'WITHDRAWALCODE', 'WITHDRAWAL_CODE']);
        
        const actionsCol = columns.find(col => {
            const h = (col.header || '').toString().toUpperCase();
            const k = (col.key || '').toString().toUpperCase();
            return col.key === 'actions' || k === 'ACTION' || h === 'OPERATIONS' || h === 'ACCIONES' || h === 'ACTIONS' || h === 'ACTION';
        });

        // 2. Identify "Obvious" Title
        const identity = skuCol || itemCol || columns.find(col => col.key !== 'checkbox' && col !== actionsCol);

        // 3. Filter Details
        const details = columns.filter(col => 
            col.key !== 'checkbox' &&
            col !== identity &&
            col !== itemCol &&
            col !== actionsCol &&
            col !== statusCol &&
            col !== stockCol &&
            col !== locationCol &&
            col !== codeCol
        );

        const isSelected = selectable && selectedIds.includes(item.id);
        return (
            <div
                key={item.id}
                onClick={() => selectable && onSelectRow && onSelectRow(item.id)}
                className={`
                    px-4 py-5 rounded-3xl border mb-3 relative transition-all duration-300 cursor-pointer overflow-hidden
                    ${isSelected
                        ? 'bg-[#1a1c1e] border-amber-500 shadow-2xl shadow-amber-500/20 scale-[0.99] ring-1 ring-amber-500/50'
                        : 'bg-white border-slate-100 shadow-sm shadow-slate-200/50 active:scale-[0.97]'
                    }
                `}
            >
                <div className={`flex flex-col gap-4 transition-all duration-300 ${isSelected ? 'text-primary' : ''}`}>
                    {/* Header: SKU & Alerta (Space Between) */}
                    <div className="flex justify-between items-start w-full">
                        <div className="animate-in fade-in slide-in-from-left-2 duration-300">
                            {identity?.render ? (
                                <div className="brightness-110">
                                    {identity.render(item, index, isSelected)}
                                </div>
                            ) : (
                                <span className="text-amber-500 text-lg font-black tracking-tight font-mono uppercase italic">{item[identity?.key]}</span>
                            )}
                        </div>
                        
                        {statusCol && (
                            <div className="animate-in fade-in slide-in-from-right-2 duration-300 scale-90 origin-right">
                                {statusCol.render ? statusCol.render(item, index, isSelected) : (
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{item[statusCol.key]}</span>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Body: Main Information & Secondary Context */}
                    <div className="flex flex-col gap-2">
                        {itemCol && identity !== itemCol && (
                            <div className="text-sm font-bold text-slate-900 uppercase tracking-tight leading-snug">
                                {itemCol.render ? itemCol.render(item, index, isSelected) : item[itemCol.key]}
                            </div>
                        )}

                        {/* Middle Line: Role/Location & Code (Space Between) */}
                        {(locationCol || codeCol) && (
                            <div className="flex justify-between items-center w-full min-h-[32px]">
                                {locationCol && (
                                    <div className="scale-95 origin-left">
                                        {locationCol.render ? locationCol.render(item, index, isSelected) : (
                                            <span className="text-xs font-medium text-slate-400 italic">{item[locationCol.key]}</span>
                                        )}
                                    </div>
                                )}
                                {codeCol && (
                                    <div className="scale-[0.85] origin-right">
                                        {codeCol.render ? codeCol.render(item, index, isSelected) : (
                                            <div className="flex flex-col items-end">
                                                <span className="text-[7px] uppercase font-black text-slate-300 mb-0.5 tracking-[0.2em]">{codeCol.header}</span>
                                                <span className="text-[10px] font-bold text-slate-500 tracking-tight">{item[codeCol.key]}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                        
                        {/* Other secondary details (if any) */}
                        {details.map((col, idx) => (
                            <div key={col.key || idx} className="flex flex-col mt-1">
                                {col.header && (
                                    <span className="text-[7px] uppercase font-black text-slate-300 mb-0.5 tracking-[0.2em]">
                                        {col.header}
                                    </span>
                                )}
                                <div className="text-[10px] font-bold text-slate-500 tracking-tight">
                                    {col.render ? col.render(item, index, isSelected) : item[col.key]}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Integrated Info: Stock & Actions (Space Between) */}
                    {(stockCol || actionsCol) && (
                        <div className="mt-1 flex justify-between items-center w-full">
                            {stockCol && (
                                <div className="animate-in fade-in slide-in-from-left-2 duration-500">
                                    {stockCol.render ? stockCol.render(item, index, isSelected) : (
                                        <span className="text-base font-black text-primary font-mono">{item[stockCol.key]}</span>
                                    )}
                                </div>
                            )}
    
                            {actionsCol && (
                                <div className="animate-in fade-in slide-in-from-right-2 duration-500" onClick={(e) => e.stopPropagation()}>
                                    <div className="scale-110">
                                        {actionsCol.render ? actionsCol.render(item, index, isSelected) : null}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className={`flex-1 flex flex-col min-h-0 ${viewMode === 'grid' ? '' : 'md:hidden'}`}>
            <div className="flex justify-between items-center mb-4 px-1 shrink-0">
                <span className="text-xs font-bold text-gray-500">{data.length} Resultados</span>
                <div className="flex items-center gap-4">
                    {headerActions}
                    {selectable && !hideSelectionOnMobile && (
                        <button onClick={onSelectAll} className="text-[10px] font-black uppercase text-primary tracking-widest">
                            {selectedIds.length === data.length ? 'Desmarcar todo' : 'Marcar todo'}
                        </button>
                    )}
                </div>
            </div>

            <div className={`flex-1 overflow-y-auto min-h-0 pl-1 pt-3 pb-10 md:pb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-1 md:gap-8 content-start ${className}`}>
                {renderMobileItem ? (
                    data.map((item, index) => renderMobileItem(item, selectedIds.includes(item.id), () => onSelectRow(item.id), index))
                ) : (
                    data.map((item, index) => renderMobileCard(item, index))
                )}

            </div>
        </div>
    );
};

export default React.memo(DataGridMobile);
